import React, { useMemo, useRef, useCallback, useState } from 'react';
import * as mapbox from 'mapbox-gl';
import { Marker as MarkerType } from '../../types/Marker';
import { useMap } from '../../hooks/useMap';
import { Marker } from './Marker';
import { getCenterPoint } from './getCenterPoint';
import { Around as TAround } from '../../types/Around';
import { Around } from './Around';

export interface MapProps {
    markers: MarkerType[];
    around?: TAround;
}

export const Map: React.FunctionComponent<MapProps> = ({ markers, around }) => {
    const [containerReady, setContainerReady] = useState(false);
    const [markersReady, setMarkersReady] = useState(markers.length < 1);

    const container = useRef();
    const initContainer = useCallback(node => {
        container.current = node;

        setContainerReady(true);
    }, []);

    const centerPoint = useMemo(() => getCenterPoint(markers), [markers]);
    const [map, bounds] = useMap(containerReady, container.current, centerPoint);

    const handleMarkerCreate = (i: number) => (marker: mapbox.Marker) => {
        // Extend the bounds to include the latest marker
        bounds.extend(marker.getLngLat());

        // Increase our tally of markers
        // markerCount.current = markerCount.current + 1;

        // If all the markers are here
        if (i === markers.length - 1) {
            const ops: mapbox.FitBoundsOptions = { padding: 200 };

            // If there's only 1 marker, we don't want to zoom in too far
            // so set a max zoom constraint
            if (markers.length <= 1) {
                ops.maxZoom = 12;
            }

            // Fit the map to the bounds.
            map.fitBounds(bounds, ops);

            setMarkersReady(true);
        }
    };

    return (
        <>
            <link rel="stylesheet" href="https://api.tiles.mapbox.com/mapbox-gl-js/v0.53.1/mapbox-gl.css" />

            <div
                ref={initContainer}
                className="map"
                id="map-container"
            />

            {!markersReady ? (
                <div className="map-overlay" />
            ) : null}

            {map ? markers.map((marker, i) => (
                <Marker
                    key={marker.key}
                    marker={marker}
                    map={map}
                    onMarkerCreate={handleMarkerCreate(i)}
                />
            )) : null}

            {around && map ?(
                <Around map={map} around={around} />
            ) : null}
        </>
    );
};

Map.displayName = 'Map';
